import type { ReactNode } from "react";
import { Link } from "react-router";
import { Alerta, Button, Card, Dialog, Row, Select, Spinner, Stack, TextArea, TextField } from "@/components";
import type { MensajeArmado } from "@/lib/mensajes";
import { MAX_VALOR_VARIABLE, tituloMensaje } from "@/lib/mensajes";
import { useMensajesProveedoresTab } from "./useMensajesProveedoresTab";

function Avisos({ armado }: { armado: MensajeArmado | null }) {
  if (!armado) return null;
  const textos = [
    armado.vacias.length > 0 && `Sin valor todavía: ${armado.vacias.join(", ")}.`,
    armado.desconocidas.length > 0 && `No existe ninguna variable ${armado.desconocidas.map((n) => `«${n}»`).join(", ")}.`,
  ].filter(Boolean);
  return textos.length > 0 ? <Alerta tono="alerta" texto={textos.join(" ")} /> : null;
}

function Previa({ armado }: { armado: MensajeArmado | null }) {
  return <p className="whitespace-pre-line rounded-control bg-superficie-2 p-3 text-chico">{armado?.texto}</p>;
}

function Fila({ titulo, valor, acciones }: { titulo: string; valor: string | null; acciones?: ReactNode }) {
  return (
    <li className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="font-medium">{`{${titulo}}`}</span>
        <span className={valor ? "text-chico" : "text-chico text-texto-suave"}>{valor ?? "Sin cargar"}</span>
      </div>
      {acciones}
    </li>
  );
}

export function MensajesProveedoresTab() {
  const t = useMensajesProveedoresTab();
  const d = t.dialogo;
  const e = t.estado;

  return (
    <Stack espacio={6}>
      <h1 className="text-pantalla font-light tracking-[-0.03em]">Mensajes a proveedores</h1>
      <p className="text-chico text-texto-suave">
        {"El mensaje que sale armado al tocar WhatsApp en la "}
        <Link to="/proveedores" className="text-primario underline">
          agenda de proveedores
        </Link>
        {": el de su etiqueta o, si no tiene, el general. Las variables van entre llaves, como {Dirección}, y se completan solas."}
      </p>
      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {e && (
        <>
          <Card>
            <section aria-labelledby="tus-variables">
              <Stack espacio={4}>
                <Row justificar="entre" envolver espacio={3}>
                  <Stack espacio={1}>
                    <h2 id="tus-variables" className="text-titulo font-light tracking-tight">Tus variables</h2>
                    <p className="text-chico text-texto-suave">El nombre sirve para todos los desarrollos; el valor es de éste.</p>
                  </Stack>
                  <Button variante="secundario" onClick={t.abrirNuevaVariable}>
                    Nueva variable
                  </Button>
                </Row>
                {e.propias.length === 0 ? (
                  <p className="text-chico text-texto-suave">Todavía no hay. Por ejemplo: "Estudio de suelos" = "No hay todavía".</p>
                ) : (
                  <ul aria-label="Tus variables" className="flex flex-col divide-y divide-borde">
                    {e.propias.map((v) => (
                      <Fila
                        key={v.id}
                        titulo={v.nombre}
                        valor={v.valor}
                        acciones={
                          <Row espacio={1}>
                            <Button variante="secundario" tamanio="sm" aria-label={`Editar ${v.nombre}`} onClick={() => t.abrirEditarVariable(v)}>
                              Editar
                            </Button>
                            <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${v.nombre}`} onClick={() => t.abrirBorrarVariable(v)}>
                              Borrar
                            </Button>
                          </Row>
                        }
                      />
                    ))}
                  </ul>
                )}
              </Stack>
            </section>
          </Card>

          <Card>
            <section aria-labelledby="del-proyecto">
              <Stack espacio={4}>
                <Stack espacio={1}>
                  <h2 id="del-proyecto" className="text-titulo font-light tracking-tight">Del proyecto</h2>
                  <p className="text-chico text-texto-suave">Salen de lo cargado en Terreno y Unidades. {"{Proveedor}"} es el nombre de a quién le escribís.</p>
                </Stack>
                <ul aria-label="Variables del proyecto" className="flex flex-col divide-y divide-borde">
                  {e.automaticas.map((v) => (
                    <Fila key={v.nombre} titulo={v.nombre} valor={v.nombre === "Proveedor" ? "El nombre del proveedor" : v.valor} />
                  ))}
                </ul>
              </Stack>
            </section>
          </Card>

          <Card>
            <section aria-labelledby="mensajes">
              <Stack espacio={4}>
                <Row justificar="entre" envolver espacio={3}>
                  <h2 id="mensajes" className="text-titulo font-light tracking-tight">Mensajes</h2>
                  <Button onClick={t.abrirNuevoMensaje}>Nuevo mensaje</Button>
                </Row>
                {e.mensajes.length === 0 ? (
                  <p className="text-chico text-texto-suave">Todavía no hay. Empezá por el general, que se usa para las etiquetas sin mensaje propio.</p>
                ) : (
                  <ul aria-label="Mensajes" className="flex flex-col gap-5">
                    {e.mensajes.map((m) => {
                      const armado = t.previa(m.texto);
                      const titulo = tituloMensaje(m);
                      return (
                        <li key={m.id} className="flex flex-col gap-2">
                          <Row justificar="entre" envolver espacio={2}>
                            <h3 className="font-semibold">{titulo}</h3>
                            <Row espacio={1}>
                              <Button variante="secundario" tamanio="sm" aria-label={`Editar el mensaje ${titulo}`} onClick={() => t.abrirEditarMensaje(m)}>
                                Editar
                              </Button>
                              <Button variante="fantasma" tamanio="sm" aria-label={`Borrar el mensaje ${titulo}`} onClick={() => t.abrirBorrarMensaje(m)}>
                                Borrar
                              </Button>
                            </Row>
                          </Row>
                          <Previa armado={armado} />
                          <Avisos armado={armado} />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Stack>
            </section>
          </Card>
        </>
      )}

      <Dialog abierto={d?.tipo === "variable"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={d?.tipo === "variable" && d.editando ? "Editar variable" : "Nueva variable"}>
        <form onSubmit={(ev) => void t.guardarVariable(ev)} noValidate>
          <Stack espacio={4}>
            <TextField
              etiqueta="Nombre"
              value={t.nombre}
              maxLength={40}
              onChange={(ev) => t.setNombre(ev.target.value)}
              error={t.errorNombre}
              ayuda={d?.tipo === "variable" && d.editando ? "Si lo cambiás, se cambia también en los mensajes que la usan." : 'Por ejemplo "Estudio de suelos".'}
            />
            <TextField etiqueta="Valor en este desarrollo" value={t.valor} maxLength={MAX_VALOR_VARIABLE} onChange={(ev) => t.setValor(ev.target.value)} ayuda="Opcional. Podés cargarlo después." />
            <Alerta texto={t.errorGuardarVariable} />
            <Row justificar="fin" espacio={2}>
              <Button type="button" variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardandoVariable}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog
        abierto={d?.tipo === "mensaje"}
        onCambio={(abierto) => !abierto && t.cerrar()}
        titulo={d?.tipo === "mensaje" && d.editando ? `Mensaje ${tituloMensaje(d.editando)}` : "Nuevo mensaje"}
        ancho="amplio"
      >
        <form onSubmit={(ev) => void t.guardarMensaje(ev)} noValidate>
          <Stack espacio={4}>
            <Select etiqueta="Para" opciones={t.opcionesPara} valor={t.para} onCambio={t.setPara} error={t.errorPara} placeholder="Elegí la etiqueta" />
            <TextArea ref={t.textoRef} etiqueta="Mensaje" value={t.texto} filas={7} onChange={(ev) => t.setTexto(ev.target.value)} error={t.errorTexto} />
            <Stack espacio={2}>
              <span className="text-chico text-texto-suave">Tocá una variable para meterla donde está el cursor:</span>
              <Row envolver espacio={1}>
                {t.variables.map((v) => (
                  <Button key={v} type="button" variante="secundario" tamanio="sm" aria-label={`Insertar ${v}`} onClick={() => t.insertar(v)}>
                    {`{${v}}`}
                  </Button>
                ))}
              </Row>
            </Stack>
            {t.texto.trim() && (
              <Stack espacio={2}>
                <span className="text-chico font-semibold">Así queda</span>
                <Previa armado={t.previaTexto} />
                <Avisos armado={t.previaTexto} />
              </Stack>
            )}
            <Alerta texto={t.errorGuardarMensaje} />
            <Row justificar="fin" espacio={2}>
              <Button type="button" variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardandoMensaje}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar-variable" || d?.tipo === "borrar-mensaje"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar">
        <Stack espacio={4}>
          <p>
            {d?.tipo === "borrar-variable" && `¿Borrar la variable "${d.variable.nombre}"? Se borra para todos los desarrollos.`}
            {d?.tipo === "borrar-mensaje" && `¿Borrar el mensaje ${tituloMensaje(d.mensaje)}?`}
          </p>
          <Alerta texto={t.errorBorrar} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cerrar}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
              Borrar
            </Button>
          </Row>
        </Stack>
      </Dialog>
    </Stack>
  );
}
